import { useMemo, useRef, useState } from "react";
import { extractAudioFeatures } from "../audio-lab/audioFeatures";
import { downloadBlob, downloadJson, formatAudioLabDateForFilename } from "../audio-lab/download";
import { createAudioLabManifest, createGlobalPresetExport, createInstrumentPresetExport } from "../audio-lab/manifest";
import {
  getAudioLabInstruments,
  getTotalAudioLabTestCount,
  type AudioLabInstrument,
  type AudioLabTestCase
} from "../audio-lab/audioLabRegistry";
import { createSpectrogramPngBlob } from "../audio-lab/spectrogram";
import {
  buildAllInstrumentsAuditZip,
  buildGlobalFeatureIndex,
  buildInstrumentAssetZip,
  buildInstrumentAuditZip,
  buildInstrumentFeatureIndex,
  buildTestBundleZip,
  type AudioLabAssetKind,
  type AudioLabProgressSnapshot
} from "../audio-lab/audioLabZip";
import {
  encodeAudioBufferToWavBlob,
  playAudioBuffer,
  renderTestCaseToAudioBuffer
} from "../audio-lab/wavExport";
import { AudioLabHeader } from "../components/audio-lab/AudioLabHeader";
import { AudioLabInstrumentCard } from "../components/audio-lab/AudioLabInstrumentCard";
import { AudioLabLogPanel, type AudioLabUiLogEntry } from "../components/audio-lab/AudioLabLogPanel";
import { AudioLabProgressPanel } from "../components/audio-lab/AudioLabProgressPanel";

type ActiveTask = {
  label: string;
  progress: AudioLabProgressSnapshot | null;
};

function createLogEntry(message: string, context?: string): AudioLabUiLogEntry {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    timestamp: new Date().toLocaleTimeString(),
    message,
    context
  };
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export function AudioLabPage() {
  const instruments = useMemo(() => getAudioLabInstruments(), []);
  const totalTestCount = useMemo(() => getTotalAudioLabTestCount(), []);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [activeTask, setActiveTask] = useState<ActiveTask | null>(null);
  const [downloadLogs, setDownloadLogs] = useState<AudioLabUiLogEntry[]>([]);
  const [errorLogs, setErrorLogs] = useState<AudioLabUiLogEntry[]>([]);
  const busy = activeTask !== null;

  const appendDownloadLog = (message: string, context?: string) => {
    setDownloadLogs((entries) => [createLogEntry(message, context), ...entries].slice(0, 24));
  };

  const appendErrorLog = (message: string, context?: string) => {
    setErrorLogs((entries) => [createLogEntry(message, context), ...entries].slice(0, 24));
  };

  const runTask = async (label: string, task: (signal: AbortSignal) => Promise<void>) => {
    if (abortControllerRef.current) {
      return;
    }

    const controller = new AbortController();

    abortControllerRef.current = controller;
    setActiveTask({ label, progress: null });

    try {
      await task(controller.signal);
    } catch (error) {
      const message = toErrorMessage(error);

      if (message.includes("cancelled") || message.includes("aborted") || controller.signal.aborted) {
        appendErrorLog("Export cancelled", label);
      } else {
        appendErrorLog(message, label);
      }
    } finally {
      abortControllerRef.current = null;
      setActiveTask(null);
    }
  };

  const createProgressHandler = (label: string) => (progress: AudioLabProgressSnapshot) => {
    setActiveTask({ label, progress });
  };

  const downloadSingleWav = (testCase: AudioLabTestCase) => {
    void runTask(`WAV ${testCase.filenameStem}`, async () => {
      const audioBuffer = await renderTestCaseToAudioBuffer(testCase);
      const wavBlob = encodeAudioBufferToWavBlob({ audioBuffer, channels: testCase.channels });

      downloadBlob(wavBlob, `${testCase.filenameStem}.wav`);
      appendDownloadLog("Downloaded WAV", `${testCase.filenameStem}.wav`);
    });
  };

  const downloadSingleSpectrogram = (testCase: AudioLabTestCase) => {
    void runTask(`Spectrogram ${testCase.filenameStem}`, async () => {
      const audioBuffer = await renderTestCaseToAudioBuffer(testCase);
      const spectrogramBlob = await createSpectrogramPngBlob({ audioBuffer });

      downloadBlob(spectrogramBlob, `${testCase.filenameStem}.spectrogram.png`);
      appendDownloadLog("Downloaded spectrogram PNG", `${testCase.filenameStem}.spectrogram.png`);
    });
  };

  const downloadSingleFeatures = (testCase: AudioLabTestCase) => {
    void runTask(`Feature JSON ${testCase.filenameStem}`, async () => {
      const audioBuffer = await renderTestCaseToAudioBuffer(testCase);
      const features = extractAudioFeatures({
        audioBuffer,
        instrumentId: testCase.instrumentId,
        note: testCase.note,
        articulation: testCase.articulation,
        durationSec: testCase.durationSec,
        sampleRate: testCase.sampleRate
      });

      downloadJson(features, `${testCase.filenameStem}.features.json`);
      appendDownloadLog("Downloaded feature JSON", `${testCase.filenameStem}.features.json`);
    });
  };

  const previewTestCase = (testCase: AudioLabTestCase) => {
    void runTask(`Preview ${testCase.filenameStem}`, async () => {
      const audioBuffer = await renderTestCaseToAudioBuffer(testCase);

      await playAudioBuffer(audioBuffer);
      appendDownloadLog("Played preview", testCase.filenameStem);
    });
  };

  const downloadTestBundle = (testCase: AudioLabTestCase) => {
    void runTask(`Bundle ${testCase.filenameStem}`, async (signal) => {
      const zipBlob = await buildTestBundleZip(testCase, {
        signal,
        onProgress: createProgressHandler(`Bundle ${testCase.filenameStem}`)
      });

      downloadBlob(zipBlob, `${testCase.filenameStem}.bundle.zip`);
      appendDownloadLog("Downloaded test bundle ZIP", `${testCase.filenameStem}.bundle.zip`);
    });
  };

  const downloadInstrumentPreset = (instrument: AudioLabInstrument) => {
    downloadJson(createInstrumentPresetExport(instrument), `${instrument.id}.preset.json`);
    appendDownloadLog("Downloaded instrument preset JSON", `${instrument.id}.preset.json`);
  };

  const downloadInstrumentFeatureIndex = (instrument: AudioLabInstrument) => {
    void runTask(`Feature Index ${instrument.name}`, async (signal) => {
      const featureIndex = await buildInstrumentFeatureIndex(instrument.id, {
        signal,
        onProgress: createProgressHandler(`Feature Index ${instrument.name}`)
      });

      downloadJson(featureIndex, `${instrument.id}.feature-index.json`);
      appendDownloadLog("Downloaded instrument feature index JSON", `${instrument.id}.feature-index.json`);
    });
  };

  const downloadInstrumentZip = (instrument: AudioLabInstrument) => {
    void runTask(`Instrument ZIP ${instrument.name}`, async (signal) => {
      const zipBlob = await buildInstrumentAuditZip(instrument.id, {
        signal,
        onProgress: createProgressHandler(`Instrument ZIP ${instrument.name}`)
      });

      downloadBlob(zipBlob, `${instrument.id}.audio-lab.zip`);
      appendDownloadLog("Downloaded instrument ZIP", `${instrument.id}.audio-lab.zip`);
    });
  };

  const downloadInstrumentAssetZip = (instrument: AudioLabInstrument, assetKind: AudioLabAssetKind) => {
    void runTask(`${assetKind.toUpperCase()} ZIP ${instrument.name}`, async (signal) => {
      const zipBlob = await buildInstrumentAssetZip(instrument.id, assetKind, {
        signal,
        onProgress: createProgressHandler(`${assetKind.toUpperCase()} ZIP ${instrument.name}`)
      });

      downloadBlob(zipBlob, `${instrument.id}.${assetKind}.zip`);
      appendDownloadLog(`Downloaded ${assetKind} ZIP`, `${instrument.id}.${assetKind}.zip`);
    });
  };

  const downloadAllZip = () => {
    void runTask("All Instruments Audit ZIP", async (signal) => {
      const zipBlob = await buildAllInstrumentsAuditZip({
        signal,
        onProgress: createProgressHandler("All Instruments Audit ZIP")
      });
      const timestamp = formatAudioLabDateForFilename();

      downloadBlob(zipBlob, `timbroam-audio-lab-${timestamp}.zip`);
      appendDownloadLog("Downloaded all instruments audit ZIP", `timbroam-audio-lab-${timestamp}.zip`);
    });
  };

  const downloadGlobalFeatureIndex = () => {
    void runTask("Global Feature Index JSON", async (signal) => {
      const featureIndex = await buildGlobalFeatureIndex({
        signal,
        onProgress: createProgressHandler("Global Feature Index JSON")
      });

      downloadJson(featureIndex, "timbroam-audio-lab.global-feature-index.json");
      appendDownloadLog("Downloaded global feature index JSON", "timbroam-audio-lab.global-feature-index.json");
    });
  };

  const cancelTask = () => {
    abortControllerRef.current?.abort();
  };

  return (
    <section className="audio-lab-page" aria-labelledby="audio-lab-title">
      <AudioLabHeader
        busy={busy}
        totalTestCount={totalTestCount}
        onDownloadAllZip={downloadAllZip}
        onDownloadGlobalPresets={() => {
          downloadJson(createGlobalPresetExport(), "timbroam-audio-lab.global-presets.json");
          appendDownloadLog("Downloaded global presets JSON", "timbroam-audio-lab.global-presets.json");
        }}
        onDownloadGlobalFeatureIndex={downloadGlobalFeatureIndex}
        onDownloadManifest={() => {
          downloadJson(createAudioLabManifest(), "timbroam-audio-lab.manifest.json");
          appendDownloadLog("Downloaded Audio Lab manifest JSON", "timbroam-audio-lab.manifest.json");
        }}
      />

      <AudioLabProgressPanel
        active={busy}
        label={activeTask?.label ?? "Ready"}
        progress={activeTask?.progress ?? null}
        onCancel={cancelTask}
      />

      <div className="audio-lab-grid" aria-label="Instrument Cards">
        {instruments.map((instrument) => (
          <AudioLabInstrumentCard
            key={instrument.id}
            instrument={instrument}
            busy={busy}
            actions={{
              preview: previewTestCase,
              presetJson: downloadInstrumentPreset,
              featureIndexJson: downloadInstrumentFeatureIndex,
              instrumentZip: downloadInstrumentZip,
              assetZip: downloadInstrumentAssetZip,
              wav: downloadSingleWav,
              spectrogram: downloadSingleSpectrogram,
              features: downloadSingleFeatures,
              testBundle: downloadTestBundle
            }}
          />
        ))}
      </div>

      <div className="audio-lab-log-grid">
        <AudioLabLogPanel title="Download Log" emptyLabel="Downloads and previews will appear here." entries={downloadLogs} />
        <AudioLabLogPanel title="Error Log" emptyLabel="No export errors yet." entries={errorLogs} tone="error" />
      </div>
    </section>
  );
}
